import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";

import styles from "./styles.module.css";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import { CompanySpecificProduct, Order, Task, WeightUnit } from "@/types";
import { Grid } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Divider from "@/components/atoms/Divider";
import {
  FinalAmountFormInput,
  FinalAmountValidationSchema,
  WeightFinalAmountFormInput,
  WeightFinalAmountValidationSchema,
} from "@/components/pages/Tasks/validation/finalAmountValidation";
import { useAuth } from "@/contexts/AuthContext";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import DrawerAccordion from "@/components/atoms/accordions/DrawerAccordion";
import { useGetContractById } from "@/services/Contracts";
import NumberInput from "@/components/atoms/NumberInput";
import Badge from "@/components/atoms/Badge";
import OrderProductDetails from "@/components/molecules/DrawerDetails/OrderProductDetails";
import WeightInput from "@/components/molecules/WeightInput";
import FinalAmountWarningModal from "@/components/organisms/FinalAmountWarningModal";
import {
  FinalQuantityEntry,
  useFinalQuantitySave,
} from "@/components/organisms/FinalAmountWarningModal/useFinalQuantitySave";
import { weightUnitOf } from "@/utils/weight";

interface FinalAmountTaskProps {
  task: Task;
  onClose: () => void;
  order?: Order;
}

const formId = "final-amount-form";

export default function FinalAmountTask({ task, onClose, order }: FinalAmountTaskProps) {
  const { t } = useTranslation();

  const { data: contract } = useGetContractById(order?.contractId as number, {
    enabled: !!order?.contractId,
  });
  const product = contract?.contractProduct.companySpecificProduct;

  // The save owns the notifications, the cache refresh and the backend's plausibility warning.
  const { save, isPending, fieldErrors, warningModal } = useFinalQuantitySave({
    mode: "single",
    taskId: task.id,
    close: onClose,
  });

  // Orders counted in kg or tonnes get the weight field; any other unit keeps the plain amount field.
  const weightUnit = weightUnitOf(order?.unit?.unit);

  return (
    <>
      <DrawerTabs defaultValue="orders" className="full flex col between h-100 py-10">
        <Group gap="20px" align="center" className={styles.taskHeader}>
          <SVGAsset name={order?.orderType.type === "BUY" ? "purchaseIndicator" : "saleIndicator"} />
          <Text size="md" display="technical" tt="uppercase" strong={true}>
            {t("enumerables:taskTypes." + task?.type)}
          </Text>
        </Group>
        {/* Form with ID to connect to external submit button */}
        <DrawerTabs.List>
          <DrawerTabs.Tab value="orders">
            <DrawerTab svgName="hamburger" label={t("tasks:orders")} />
          </DrawerTabs.Tab>
        </DrawerTabs.List>

        <DrawerTabs.Panel value="orders" className={styles.tabPanel}>
          {order && weightUnit && (
            <WeightFinalAmountForm
              order={order}
              weightUnit={weightUnit}
              product={product}
              save={save}
              isPending={isPending}
              saveError={fieldErrors[order.id]}
            />
          )}
          {order && !weightUnit && (
            <AmountFinalAmountForm
              order={order}
              product={product}
              save={save}
              isPending={isPending}
              saveError={fieldErrors[order.id]}
            />
          )}
        </DrawerTabs.Panel>
      </DrawerTabs>
      <FinalAmountWarningModal {...warningModal} />
    </>
  );
}

interface FinalAmountFormProps {
  order: Order;
  product?: CompanySpecificProduct | null;
  save: (entries: FinalQuantityEntry[]) => void;
  isPending: boolean;
  /** Why the last save of this order failed, as a translation key. A validation message on the field goes first. */
  saveError?: string;
}

interface WeightFinalAmountFormProps extends FinalAmountFormProps {
  weightUnit: WeightUnit;
}

/** The form for a kg or tonnes order: the weight is sent as typed, and the backend decides whether it is plausible. */
const WeightFinalAmountForm = ({
  order,
  weightUnit,
  product,
  save,
  isPending,
  saveError,
}: WeightFinalAmountFormProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<WeightFinalAmountFormInput>({
    resolver: zodResolver(WeightFinalAmountValidationSchema),
    // The weight field has no blur event, so the form is checked as the weight is typed.
    mode: "onChange",
    values: {
      weight: { amount: order.amount || "", unit: weightUnit },
    },
  });

  const onSubmit = ({ weight }: WeightFinalAmountFormInput) => save([{ orderId: order.id, weight }]);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className={styles.tabPanel}>
      <OrderAccordion order={order} product={product}>
        {/* On a phone the label goes above, as the weight field and its unit toggle need the drawer's full width. */}
        <Grid.Col span={{ base: 12, xs: 3 }}>
          <Text size="sm" c="dimmed">
            {t("tasks:finalAmount")}
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, xs: 9 }}>
          {/* Save wraps below the field once the row is too narrow for both. */}
          <Group align="flex-start">
            <Controller
              control={control}
              name="weight"
              render={({ field, fieldState }) => (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <WeightInput
                    data-testid="final-amount-input"
                    aria-label={t("tasks:finalAmount")}
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error ? t(fieldState.error.message as string) : saveError && t(saveError)}
                  />
                </div>
              )}
            />
            <SaveButton disabled={!isValid || isPending} loading={isPending} />
          </Group>
        </Grid.Col>
      </OrderAccordion>
    </form>
  );
};

/** The form for the other units (stk, m3, meter): the plain amount field, held to the order's agreed deviation. */
const AmountFinalAmountForm = ({ order, product, save, isPending, saveError }: FinalAmountFormProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
  } = useForm<FinalAmountFormInput>({
    resolver: zodResolver(FinalAmountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      amount: order.amount || 0,
    },
  });

  const onSubmit = (data: FinalAmountFormInput) => {
    const amount = data.amount;
    const originalAmount = order.amount;
    const amountDeviation = order.amountDeviation || 0;

    const deviation: number = (originalAmount / 100) * amountDeviation;
    const lowEnd: number = originalAmount - deviation;
    const highEnd: number = originalAmount + deviation;

    if (amount > highEnd || amount < lowEnd) {
      setError(`amount`, {
        type: "manual",
        message: t("tasks:legalFinalAmount") + lowEnd + order.unit?.unit + " - " + highEnd + order.unit?.unit,
      });
      return;
    }
    save([{ orderId: order.id, amount }]);
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className={styles.tabPanel}>
      <OrderAccordion order={order} product={product}>
        <Grid.Col span={3}>
          <Text size="sm" c="dimmed">
            {t("tasks:finalAmount")}
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Group>
            <Controller
              control={control}
              name={"amount"}
              render={({ field, fieldState }) => {
                return (
                  <Group justify={"flex-start"} align={"flex-start"}>
                    <span
                      style={{
                        position: "relative",
                        display: "inline-block",
                        overflow: "hidden",
                        maxWidth: 190,
                      }}
                    >
                      <NumberInput
                        id={"amount"}
                        data-testid="final-amount-input"
                        hideControls
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={
                          fieldState.error
                            ? fieldState.error.type === "manual"
                              ? fieldState.error.message
                              : t(fieldState.error.message as string)
                            : saveError && t(saveError)
                        }
                      />
                      <Badge
                        size={"sm"}
                        h={35}
                        radius={0}
                        color={"primary"}
                        pos={"absolute"}
                        top={0}
                        right={0}
                        bottom={0}
                      >
                        {order.unit?.unit}
                      </Badge>
                    </span>

                    <SaveButton disabled={!isValid || isPending} loading={isPending} />
                  </Group>
                );
              }}
            />
          </Group>
        </Grid.Col>
      </OrderAccordion>
    </form>
  );
};

interface OrderAccordionProps {
  order: Order;
  product?: CompanySpecificProduct | null;
  /** The final amount row, below the order's product details. */
  children: ReactNode;
}

const OrderAccordion = ({ order, product, children }: OrderAccordionProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <DrawerAccordion unstyled={true} defaultValue={"order"}>
      <DrawerAccordion.Item value={"order"}>
        <DrawerAccordion.Control>
          <Text size="sm" display="technical" tt="uppercase" strong={true}>
            {t("common:order")} {order.id}
          </Text>
        </DrawerAccordion.Control>
        <DrawerAccordion.Panel>
          <Grid>
            <OrderProductDetails
              defaultProductName={`${order.salesItem.nameTranslated?.[user?.language.id as number] ?? order.salesItem.salesItem} - ${order.properties[0] ? order.properties[0].value : ""}`}
              product={product}
            />
            <Grid.Col>
              <Divider />
            </Grid.Col>
            {children}
          </Grid>
        </DrawerAccordion.Panel>
      </DrawerAccordion.Item>
    </DrawerAccordion>
  );
};

interface SaveButtonProps {
  disabled: boolean;
  loading: boolean;
}

const SaveButton = ({ disabled, loading }: SaveButtonProps) => {
  const { t } = useTranslation();

  return (
    <CTA
      data-testid="final-amount-submit-btn"
      size="sm"
      intent="secondary"
      type={"submit"}
      form={formId}
      disabled={disabled}
      loading={loading}
    >
      {t("actions:save")}
    </CTA>
  );
};
